import { FilterState } from '../types/types';

/**
 * Convert filter state to URL parameters
 */
export const filtersToUrlParams = (filters: FilterState, searchTerm?: string): URLSearchParams => {
  const params = new URLSearchParams();
  
  // Add shift types
  if (filters.shiftTypes.length > 0) {
    filters.shiftTypes.forEach(type => {
      params.append('shiftType', type);
    });
  }
  
  // Add systems
  if (filters.systems.length > 0) {
    filters.systems.forEach(system => {
      params.append('system', system);
    });
  }
  
  // Add confidence range
  if (filters.confidenceRange[0] > 0 || filters.confidenceRange[1] < 5) {
    params.set('minConfidence', filters.confidenceRange[0].toString());
    params.set('maxConfidence', filters.confidenceRange[1].toString());
  }
  
  // Add entities
  if (filters.selectedEntities.length > 0) {
    filters.selectedEntities.forEach(entity => {
      params.append('entity', entity);
    });
  }
  
  // Add reporting periods
  if (filters.reportingPeriods && filters.reportingPeriods.length > 0) {
    params.set('rp', filters.reportingPeriods.join(','));
  }
  
  // Add statuses
  if (filters.statuses && filters.statuses.length > 0) {
    params.set('status', filters.statuses.join(','));
  }
  
  // Add implementation stages
  if (filters.implementationStages && filters.implementationStages.length > 0) {
    params.set('stage', filters.implementationStages.join(','));
  }
  
  // Add impact modules
  if (filters.selectedModules && filters.selectedModules.length > 0) {
    params.set('modules', filters.selectedModules.join(','));
  }
  
  // Add impact streams
  if (filters.selectedStreams && filters.selectedStreams.length > 0) {
    params.set('streams', filters.selectedStreams.join(','));
  }
  
  // Add search term if provided
  if (searchTerm && searchTerm.trim() !== '') {
    params.set('search', searchTerm);
  }
  
  return params;
};

/**
 * Convert URL parameters to filter state
 */
export const urlParamsToFilters = (params: URLSearchParams): {
  filters: Partial<FilterState>;
  searchTerm?: string;
} => {
  const filters: Partial<FilterState> = {};
  
  // Get shift types
  const shiftTypes = params.getAll('shiftType');
  if (shiftTypes.length > 0) {
    filters.shiftTypes = shiftTypes;
  }
  
  // Get systems
  const systems = params.getAll('system');
  if (systems.length > 0) {
    filters.systems = systems;
  }
  
  // Get confidence range
  const minConfidence = params.get('minConfidence');
  const maxConfidence = params.get('maxConfidence');
  if (minConfidence !== null || maxConfidence !== null) {
    filters.confidenceRange = [
      minConfidence !== null ? parseInt(minConfidence) : 0,
      maxConfidence !== null ? parseInt(maxConfidence) : 5
    ];
  }
  
  // Get entities
  const entities = params.getAll('entity');
  if (entities.length > 0) {
    filters.selectedEntities = entities;
  }
  
  // Get reporting periods
  const reportingPeriodsParam = params.get('rp');
  if (reportingPeriodsParam) {
    filters.reportingPeriods = reportingPeriodsParam.split(',').filter(p => p.trim());
  }
  
  // Get statuses
  const statusesParam = params.get('status');
  if (statusesParam) {
    filters.statuses = statusesParam.split(',').filter(s => s.trim());
  }
  
  // Get implementation stages
  const stagesParam = params.get('stage');
  if (stagesParam) {
    filters.implementationStages = stagesParam.split(',').filter(s => s.trim());
  }
  
  // Get impact modules
  const modulesParam = params.get('modules');
  if (modulesParam) {
    filters.selectedModules = modulesParam.split(',').filter(m => m.trim());
  }
  
  // Get impact streams
  const streamsParam = params.get('streams');
  if (streamsParam) {
    filters.selectedStreams = streamsParam.split(',').filter(s => s.trim());
  }
  
  // Get search term
  const searchTerm = params.get('search') || undefined;
  
  return { filters, searchTerm };
};

/**
 * Get evidence ID from URL
 */
export const getEvidenceIdFromUrl = (search: string): string | null => {
  const params = new URLSearchParams(search);
  return params.get('id');
};

/**
 * Create URL for sharing evidence list
 */
export const createShareableEvidenceListUrl = (filters: FilterState, searchTerm?: string): string => {
  const baseUrl = window.location.origin;
  const params = filtersToUrlParams(filters, searchTerm);
  return `${baseUrl}/evidence?${params.toString()}`;
};

/**
 * Create URL for sharing a single evidence item
 */
export const createShareableEvidenceItemUrl = (evidenceId: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/evidence/item?id=${evidenceId}`;
}; 